import React, { useEffect, useState, useRef } from "react";
import { Eye, EyeOff, Loader, Lock } from "lucide-react";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { can_visit, getPractice, getSecureData, notify, setSecureData, usPhoneFormat, validatePassword } from "../../helpers";
import Spacing from "../Spacing";
import Inputmask from "inputmask";
import { checkUnityEhrCredentials, getProfileData, updateUserProfile } from "../../services/profile";
import Select from "react-select";
import { useProfile } from "../../context/ProfileContext";

export default function UserProfile({ selectedPracticeChange, fetchChangePracticeData }) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [isValid, setIsValid] = useState(true);
  const [oldPassword, setOldPassword] = useState(false);
  const [newPassword, setNewPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ehrPassword, setEhrPassword] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const ehrSectionRef = useRef(null);
  const [highlightEhr, setHighlightEhr] = useState(false);
  const { updateProfile } = useProfile();

  useEffect(() => {
    const checkRedirect = async () => {
      const justLoggedIn = await getSecureData("_just_logged_in");
      if (justLoggedIn && can_visit('ehr_veradigm')) {
        notify("success", "Please complete your EHR profile.", 2);
        setHighlightEhr(true);
        ehrSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        await setSecureData("_just_logged_in", false); // reset flag

        // Auto remove highlight after 1 minute
        setTimeout(() => {
          setHighlightEhr(false);
        }, 60000);
      }
    };

    checkRedirect();
  }, [account_id]);

  const defaultValues = {
    account_id: account_id,
    location_id: location_id,
    user_id: user_id,
    last_name: "",
    first_name: "",
    contact: "",
    email: "",
    user_name: "",
    current_password: "",
    new_password: "",
    confirm_password: "",
    ehr_type: "",
    ehr_username: "",
    ehr_password: "",
  }
  const [formData, setFormData] = useState(defaultValues);
  const [ehrType] = useState([
    {value: 'ECW', label: 'ECW', },
    { value: 'MDLand', label: 'MDLand', },
    { value: 'VeradigmEHR', label: 'VeradigmEHR', },
    { value: 'AthenaHealth', label: 'AthenaHealth', }
  ]);

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const response = await getProfileData();
      if (response?.data) {
        setUserProfileData(response?.data);
      } else {
        setFormData(defaultValues);
      }
    } catch (error) {
      console.error("Error fetching loadAllRoles data:", error);
    }
  };

  const setUserProfileData = (data) => {
    setFormData({
      ...formData,
      last_name: data?.last_name,
      first_name: data?.first_name,
      contact: data?.contact,
      email: data?.email,
      user_name: data?.user_name,
      ehr_type: data?.ehr_type,
      ehr_username: data?.ehr_username,
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setIsValid(validatePassword(value));
  };

  const handleEhrPasswordChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "contact") {
      const phoneInputs = document.querySelectorAll(".us_phone");
      Inputmask({
        mask: "(999) 999-9999",
        showMaskOnHover: false,
        showMaskOnFocus: true,
      }).mask(phoneInputs);
    }
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (formData.new_password !== "" && !validatePassword(formData.new_password)) {
      setLoading(false);
      setIsValid(false);
      return;
    }

    if (formData.current_password === "" && formData.new_password !== "" && formData.confirm_password !== "") {
      setError("For change password you need to provide current password.");
      setLoading(false);
      return;
    }
    if (formData.current_password !== "" && formData.new_password !== "" && formData.confirm_password !== "") {
      if (formData.new_password !== formData.confirm_password) {
        setError("New password and confirm password do not match.");
        setLoading(false);
        return;
      }
    }

    setError("");

    try {
      const result = await updateUserProfile(formData)

      if (result?.detail) {
        notify("error", result?.detail);
      } else if (result?.data?.status === "success") {
        notify("success", result?.data?.message);
        setFormData({
          ...formData,
          current_password: "",
          new_password: "",
          confirm_password: "",
          ehr_password: "",
        });
        updateProfile(formData);
        setHighlightEhr(false);
      } else {
        notify("error", result?.data?.message);
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setLoading(false);
    }
  };


  const handleCheckPassword = async () => {
    const payload = {
      account_id: account_id,
      ehr_username: formData.ehr_username?.trim(),
      ehr_password: formData.ehr_password?.trim(),
    };

    if (!payload.ehr_username || !payload.ehr_password) {
      notify("error", "Please enter both EHR Username and Password.");
      return;
    }

    setIsChecking(true);

    try {
      const res = await checkUnityEhrCredentials(payload);
      if (res?.data?.status === "success") {
       notify("success", res?.data?.message);
      } else {
        notify("error", res?.data?.message);
      }
    } catch (err) {
      console.error("Error checking credentials:", err);
      notify("error", err);
    } finally {
      setIsChecking(false);
    }
  };


  useDocumentTitle("Profile");

  return (
    <div className="user-profile-section">
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          Profile
        </h2>
      </div>

      <div className="docpro_card">
        <form onSubmit={handleSubmit} autoComplete="off" className="relative">
          <h6 className="docpro_fs_18 docpro_sky_color">Basic Information :</h6>
          <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_3">
            <div className="docpro_grid_item">
              <label className="docpro_fw_500 docpro_primary_color">
                Last Name <span className="docpro_red_color">*</span>
              </label>
              <input
                type="text"
                className="docpro_form_field size_lg"
                name="last_name"
                autoComplete="off"
                required
                onChange={(e) => { handleChange(e) }}
                defaultValue={formData.last_name}
              />
            </div>
            <div className="docpro_grid_item">
              <label className="docpro_fw_500 docpro_primary_color">
                First Name <span className="docpro_red_color">*</span>
              </label>
              <input
                type="text"
                className="docpro_form_field size_lg"
                name="first_name"
                autoComplete="off"
                required
                onChange={(e) => { handleChange(e) }}
                defaultValue={formData.first_name}
              />
            </div>
            <div className="docpro_grid_item">
              <label className="docpro_fw_500 docpro_primary_color">Contact</label>
              <input
                type="text"
                className="docpro_form_field size_lg us_phone"
                name="contact"
                autoComplete="off"
                onChange={(e) => { handleChange(e) }}
                value={formData.contact ? usPhoneFormat(formData.contact) : ""}
                maxLength={20}
                placeholder="(123) 123 1234"
              />
            </div>

            <div className="docpro_grid_item">
              <label className="docpro_fw_500 docpro_primary_color">
                Email <span className="docpro_red_color">*</span>
              </label>
              <input
                type="email"
                name="email"
                className="docpro_form_field size_lg"
                autoComplete="off"
                required
                onChange={(e) => { handleChange(e) }}
                defaultValue={formData.email}
              />
            </div>
            <div className="docpro_grid_item">
              <label className="docpro_fw_500 docpro_primary_color">
                Username <span className="docpro_red_color">*</span>
              </label>
              <input
                type="text"
                className="docpro_form_field size_lg"
                autoComplete="off"
                readOnly
                disabled
                value={formData.user_name}
              />
            </div>
          </div>

          <Spacing lg="30" sm="30" />
          <h6 className="docpro_fs_18 docpro_sky_color">Password Information :</h6>
          <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_3">
            <div className="docpro_grid_item">
              <label className="docpro_fw_500 docpro_primary_color"> Current Password </label>
              <div className="docpro_form_field_with_icon">
                <input
                  name="current_password"
                  type={oldPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="docpro_form_field size_lg"
                  onChange={(e) => { handleChange(e) }}
                  autoComplete="new-password"
                  minLength={4}
                />
                <Lock width={18} height={18} className="docpro_form_field_icon" />
                {oldPassword ?
                  <Eye onClick={() => setOldPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                  :
                  <EyeOff onClick={() => setOldPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                }
              </div>
            </div>
            <div className="docpro_grid_item">
              <label className="docpro_fw_500 docpro_primary_color"> New Password </label>
              <div className="docpro_form_field_with_icon">
                <input
                  name="new_password"
                  type={newPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className={`docpro_form_field size_lg ${!isValid && 'docpro_border_red'}`}
                  onChange={handlePasswordChange}
                  // onChange={(e) => {handleChange(e)}}
                  autoComplete="new-password"
                  minLength={8}
                />
                <Lock width={18} height={18} className="docpro_form_field_icon"
                />
                {newPassword ?
                  <Eye onClick={() => setNewPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                  :
                  <EyeOff onClick={() => setNewPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                }
              </div>
            </div>
            <div className="docpro_grid_item">
              <label className="docpro_fw_500 docpro_primary_color"> Confirm Password </label>
              <div className="docpro_form_field_with_icon">
                <input
                  name="confirm_password"
                  type={confirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className={`docpro_form_field size_lg ${!isValid && 'docpro_border_red'}`}
                  onChange={handlePasswordChange}
                  // onChange={(e) => {handleChange(e)}}
                  autoComplete="new-password"
                  minLength={8}
                />
                <Lock width={18} height={18} className="docpro_form_field_icon" />
                {confirmPassword ?
                  <Eye onClick={() => setConfirmPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                  :
                  <EyeOff onClick={() => setConfirmPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                }
              </div>
            </div>
          </div>
          <Spacing lg="15" sm="15" />
          {error && <div className="text-red-500 text-sm mb-0 mt-1">{error}</div>}
          {(!isValid) && (
            <p className="text-red-500 text-sm mb-0 mt-1">
              Password must be at least 12 characters and include uppercase, lowercase, number, and special character.
            </p>
          )}

          <div className="user-profile-ehr-section">
            <Spacing lg="30" sm="30" />
            <h6 className="docpro_fs_18 docpro_sky_color">EHR Information :</h6>
            <div
              ref={ehrSectionRef}
              className={`docpro_grid docpro_grid_gap_20_16 docpro_grid_col_3 ${highlightEhr ? "highlight-ehr" : ""}`}
            >
              <div className="docpro_grid_item">
                <label className="docpro_fw_500 docpro_primary_color"> EHR Type </label>
                <Select
                  name="ehr_type"
                  options={ehrType}
                  onChange={(selectedOption) => {
                    setFormData({ ...formData, ehr_type: selectedOption.value });
                  }}
                  placeholder="Search..."
                  isSearchable={true}
                  value={ehrType.find(
                    (option) => option.value === formData.ehr_type
                  )}
                  className="react-select-container w-full"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      borderRadius: '8px',
                      borderColor: 'var(--border)',
                    }),
                  }}
                />
              </div>
              <div className="docpro_grid_item">
                <label className="docpro_fw_500 docpro_primary_color"> EHR Username </label>
                <input
                  type="text"
                  className="docpro_form_field size_lg"
                  name="ehr_username"
                  autoComplete="off"
                  onChange={(e) => { handleChange(e) }}
                  defaultValue={formData.ehr_username}
                />
              </div>
              <div className="docpro_grid_item" style={{display: "flex", alignItems: "flex-end", gap: "5px",  justifyContent: "center"}}>
                <div style={{ width: "100%" }}>
                  <label className="docpro_fw_500 docpro_primary_color"> EHR Password </label>
                  <div className="docpro_form_field_with_icon">
                    <input
                      name="ehr_password"
                      type={ehrPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={`docpro_form_field size_lg`}
                      onChange={handleEhrPasswordChange}
                      autoComplete="new-password"
                      minLength={4}
                    />
                    <Lock width={18} height={18} className="docpro_form_field_icon" />
                    {ehrPassword ?
                      <Eye onClick={() => setEhrPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                      :
                      <EyeOff onClick={() => setEhrPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                    }
                  </div>
                </div>
                <div>   
                  <button
                    type="button"
                    onClick={handleCheckPassword}
                    style={{background: "#9bcfc2", color: "#000", height: "40px"}}
                    className={`docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg`}
                    disabled={!formData.ehr_username || !formData.ehr_password?.trim()}
                  >
                    Check
                  </button>
                </div>
              </div>
            </div>
          </div>

          <Spacing lg="32" sm="32" />
          <div className="docpro_modal_btns">
            <button
              type="submit"
              className={`docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg`}
              style={{ pointerEvents: `${loading ? "none" : "initial"}` }}
            >
              <span>Update</span>
              {loading && (
                <span className="docpro_loading">
                  <Loader width={20} height={20} />
                </span>
              )}
            </button>
          </div>

          {loading && (
            <div className="absolute top-0 left-0 w-full" style={{ height: "100%", zIndex: 3 }}>
              <div className="flex items-center loading" style={{ width: "100%", height: "100%", justifyContent: "center", background: "rgb(255 255 255 / 63%)" }}>
                <span className="docpro_loading" style={{ position: "inherit" }}>
                  <Loader width={46} height={46} style={{ color: "var(--accent)" }} />
                </span>
              </div>
            </div>
          )}
        </form>

      </div>
    </div>
  );
}
